import * as _ from "lodash";
import { ClientRepo as clientRepo } from "@services/clients/client.repo";
import { ReportingService as reportingService } from "@common/reporting.service";
import * as moment from "moment";
import { ReportingParams } from "@app/reporting/reportingParams";
import { IS_RECONCILED_FLAG } from "@constants/reporting";
import { AuthService } from "@common/auth.service";
import { Constants } from "@constants/shared";

class PorfolioAdminController implements angular.IOnInit {
  private client: any;
  public clientPortfolios: Array<any>;
  public selectedClientPortfolios: Array<number>;
  public tempSelectedClientPortfolios: Array<number>;
  public sources: Array<string>;
  public oldestPVDate: Date;
  public oldestTradeDate: any;
  public oldestCashDate: any;
  public showOptions = true;

  public currencyList: Array<string>;
  public selectedCurrency: string;

  public isLoading: boolean = false;
  public loading: boolean = false;
  public isReconciled: boolean;
  public selectedLocality;
  public localityList;

  public clients;
  public selectedClient;
  public clientNumber;
  public searchText;

  //feature flag
  public showAttribution: boolean = false;

  public isBenchmarkAttributionEnabled = false;

  /** @ngInject */
  constructor(
    private clientRepo: clientRepo,
    private $stateParams: angular.ui.IStateParamsService,
    private reportingService: reportingService,
    private notifyjs,
    private $state: angular.ui.IStateService,
    private healthService: any,
    private authService: AuthService
  ) {}

  $onInit() {
    // Block access for ClientDetailsViewOnly role - redirect to clients listing page
    if (this.authService.isClientDetailsViewOnly()) {
      this.$state.go("root.portfolioManagement.clients");
      return;
    }

    this.currencyList = ["MUR", "EUR", "GBP", "USD", "CHF"];
    this.localityList = [
      { name: "Combined", value: "" },
      { name: "Mauritius", value: "Local" },
      { name: "International", value: "International" },
    ];
    this.selectedCurrency = "MUR";
    this.selectedLocality = "";
    this.clientNumber = this.$stateParams["clientNumber"];
    this.isReconciled = sessionStorage.getItem(IS_RECONCILED_FLAG) === "true";
    this.getClientDetails(this.clientNumber);
    this.getClients();
    const env = Constants.getEnvironment();
    this.showAttribution = env === "Local" || env === Constants.STAGE;
  }

  public enableOptions() {
    this.showOptions = true;
  }

  public disableOptions() {
    this.showOptions = false;
  }

  public getSelectedText() {
    if (
      this.tempSelectedClientPortfolios.length == this.clientPortfolios.length
    )
      return "All Client Portfolios Displayed";

    return `${this.tempSelectedClientPortfolios.length} of ${this.clientPortfolios.length} Portfolios Displayed`;
  }

  public onSelectClose() {
    this.selectedClientPortfolios = this.tempSelectedClientPortfolios;
  }

  public getSourceFileDate(source: string, fileType: string) {
    for (var p of this.client.portfolios) {
      for (var a of p.accounts) {
        // Get the source PV date:
        if (a.accountSourceName == source) {
          if (fileType == "PV") {
            if (a.latestPVFileDate != null) return new Date(a.latestPVFileDate);
          }
        }
      }
    }
  }

  public generateReport() {
    this.isLoading = true;
    let yesterday = moment().add(-1, "days").format("YYYY-MM-DD");
    const reportingParams = new ReportingParams();
    reportingParams.reportType = "portfoliosummary";
    reportingParams.currency = this.selectedCurrency;
    reportingParams.fromDate = null;
    reportingParams.toDate = yesterday;
    reportingParams.portfolioIds = this.selectedClientPortfolios;
    reportingParams.displayLocalPerformance = true;
    reportingParams.displayInternationalPerformance = true;
    reportingParams.displayMonthlyPerformance = false;
    reportingParams.displayLineItems = true;
    reportingParams.isReconciled = this.isReconciled;
    this.reportingService
      .generateReport(reportingParams)
      .then((res) => {
        this.isLoading = false;
      })
      .catch(() => {
        this.isLoading = false;
        this.notifyjs(
          "The report could not be generated. Please try again later or contact the System Administrator for assistance.",
          "error"
        );
      });
  }

  getClients = () => {
    this.clientRepo
      .getClientsForSelection()
      .then((res) => {
        // Map the selection endpoint response to match the expected structure
        const clientSelections = res.data as Array<{ id: number; clientNumber: string; displayName: string }>;
        this.clients = clientSelections.map((c) => ({
          id: c.id,
          clientNumber: c.clientNumber,
          name: c.displayName,
          status: "Active" // Selection endpoint only returns active clients
        }));
        if (!this.selectedClient) {
          this.selectedClient = _.first(
            this.clients.filter((c) => c.clientNumber == this.clientNumber)
          );
          let index = _.findIndex(
            this.clients,
            (c: any) => c.clientNumber == this.clientNumber
          );
          if (index > -1) {
            this.clients.splice(index, 1); //, this.clients.splice(index, 1)[0]);
            this.clients.unshift(this.selectedClient);
          }
        }
      })
      .catch((err) => {
        this.notifyjs("Failed to load client details");
      });
  };

  getClientDetails = (clientNumber) => {
    this.loading = true;
    this.clientRepo.getClient(clientNumber, true).then((val) => {
      this.client = val.data;
      this.clientPortfolios = _.map(
        val.data["portfolios"],
        (portfolio: any) => {
          return {
            id: Number(portfolio.portfolioId),
            name: portfolio.name,
            isManaged: portfolio.isManaged,
            source: _.orderBy(
              _.keys(
                _.groupBy(portfolio.accounts, (a: any) => {
                  return a.accountSourceName;
                })
              ),
              (source) => {
                return source;
              }
            ).join(","),
            accounts: portfolio.accounts,
          };
        }
      );
      this.tempSelectedClientPortfolios = _.map(
        val.data["portfolios"],
        (portfolio: any) => {
          return Number(portfolio.portfolioId);
        }
      );
      this.selectedClientPortfolios = this.tempSelectedClientPortfolios;
      this.sources = _.orderBy(
        _.keys(
          _.groupBy(this.clientPortfolios, (p) => {
            return p.source;
          })
        ),
        (source) => {
          return source;
        }
      );
      this.evaluateIfBenchmarkTabIsEnabled(this.client);
    })
    .catch((err) => {
      this.notifyjs("Failed to load client details", "error");
    })
    .finally(() => {
      this.loading = false;
    });
  };

  getNewClientDetails = () => {
    let clientNumber = this.selectedClient.clientNumber;
    this.$state.go("root.portfolioManagement.clients.portfolio", {
      clientNumber,
    });
  };

  public evaluateIfBenchmarkTabIsEnabled(client: any) {
    this.isBenchmarkAttributionEnabled =
      client.primaryBenchmarkColumnName !== "Return Target (%)";
  }

  // Role-based permission methods - delegate to AuthService
  public canEditClients(): boolean {
    return this.authService.canEditClients();
  }

  public isViewOnly(): boolean {
    return this.authService.isViewOnly();
  }
}

export const portfolioadmin: angular.IComponentOptions = {
  template: require("./portfolioadmin.html"),
  controller: PorfolioAdminController,
};



// WEBPACK FOOTER //
// ./src/app/portfolioadmin/portfolioadmin.ts